import { useState, useEffect } from "react";
import { Check, ChevronRight, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type { OnboardingChecklist } from "@/types/api";
import { WelcomeStep } from "./welcome-step";
import { IntegrationStep } from "./integration-step";
import { CloudTrailStep } from "./cloudtrail-step";
import { AgentDeployStep } from "./agent-deploy-step";
import { NotificationStep } from "./notification-step";
import { VerifyStep } from "./verify-step";
import { ConnectMicrosoft } from "@/components/integrations/connect-microsoft";

/* ── Step definitions ─────────────────────────────────────── */

interface WizardStepDef {
  key: string;
  label: string;
  /** Maps to OnboardingStep.category or special match */
  category: string | null;
  optional?: boolean;
}

// ponytail: MVP scope (Yosef 2026-06-24) — only validated sources onboard here.
// AWS CloudTrail and GCP Audit are hidden for launch; re-add the two step lines
// to restore them (their render cases below are kept intact). No GW step yet —
// Google Workspace onboards via the Add-Integration dialog until it's validated.
const WIZARD_STEPS: WizardStepDef[] = [
  { key: "welcome", label: "Welcome", category: "setup" },
  { key: "microsoft", label: "Connect Microsoft", category: "identity" },
  { key: "endpoint", label: "Endpoint Agent", category: "endpoint" },
  { key: "notifications", label: "Notifications", category: "notifications" },
  { key: "verify", label: "Verify", category: "setup" },
];

/* ── Helpers ──────────────────────────────────────────────── */

function isStepCompleted(
  step: WizardStepDef,
  checklist: OnboardingChecklist | null,
): boolean {
  if (step.key === "welcome" || step.key === "verify") return true;
  if (!checklist) return false;

  if (step.key === "microsoft") {
    return checklist.steps.some(
      (s) =>
        (s.category === "identity" ||
          s.category === "email" ||
          s.name === "m365_defender") &&
        s.status === "completed",
    );
  }

  if (step.key === "notifications") {
    return checklist.steps.some(
      (s) => s.name === "Configure Notifications" && s.status === "completed",
    );
  }

  return checklist.steps.some(
    (s) => s.category === step.category && s.status === "completed",
  );
}

function countCompleted(checklist: OnboardingChecklist | null): number {
  return checklist?.steps.filter((s) => s.status === "completed").length ?? 0;
}

function countTotal(checklist: OnboardingChecklist | null): number {
  return checklist?.steps.length ?? 0;
}

/* ── Props ────────────────────────────────────────────────── */

interface SetupWizardProps {
  checklist: OnboardingChecklist | null;
  isLoading: boolean;
  onSkip: () => void;
  onComplete: () => void;
}

/* ── Component ────────────────────────────────────────────── */

export function SetupWizard({
  checklist,
  isLoading,
  onSkip,
  onComplete,
}: SetupWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [hasAutoAdvanced, setHasAutoAdvanced] = useState(false);

  // Auto-advance past welcome if user has prior progress
  useEffect(() => {
    if (hasAutoAdvanced || !checklist) return;
    const completed = countCompleted(checklist);
    if (completed > 0 && currentStep === 0) {
      const firstIncomplete = WIZARD_STEPS.findIndex(
        (s, i) => i > 0 && i < WIZARD_STEPS.length - 1 && !isStepCompleted(s, checklist),
      );
      setCurrentStep(firstIncomplete > 0 ? firstIncomplete : WIZARD_STEPS.length - 1);
    }
    setHasAutoAdvanced(true);
  }, [checklist, currentStep, hasAutoAdvanced]);

  /* Loading state */
  if (isLoading) {
    return (
      <div className="flex min-h-screen flex-col bg-background">
        <header className="flex items-center justify-between border-b px-6 py-4">
          <div className="flex items-center gap-2">
            <Skeleton className="h-6 w-6 rounded" />
            <Skeleton className="h-5 w-32" />
          </div>
          <Skeleton className="h-5 w-24" />
        </header>
        <Skeleton className="h-1 w-full" />
        <div className="flex flex-1 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      </div>
    );
  }

  const totalSteps = countTotal(checklist);
  const completedSteps = countCompleted(checklist);
  const progressPct = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

  const step = WIZARD_STEPS[currentStep];
  const showFooter = step.key !== "welcome" && step.key !== "verify";

  function goNext() {
    if (currentStep < WIZARD_STEPS.length - 1) {
      setCurrentStep((p) => p + 1);
    }
  }

  function goBack() {
    if (currentStep > 0) {
      setCurrentStep((p) => p - 1);
    }
  }

  /* ── Render step content ──────────────────────────────── */
  function renderStepContent() {
    const s = WIZARD_STEPS[currentStep];
    const completed = isStepCompleted(s, checklist);

    switch (s.key) {
      case "welcome":
        return <WelcomeStep onContinue={goNext} />;
      case "microsoft":
        // No onConnected auto-advance (#177c): the connected view surfaces the
        // Entra licence result (full vs audit-only + P1 upsell); the customer
        // reviews it, then advances via the footer "Continue".
        return (
          <div className="space-y-4">
            <ConnectMicrosoft />
          </div>
        );
      case "cloudtrail":
        return <CloudTrailStep isCompleted={completed} />;
      case "gcp_audit":
        // key={s.key} forces a remount when navigating away and back — without
        // it React reuses the IntegrationStep instance and local state bleeds.
        return (
          <IntegrationStep
            key={s.key}
            stepKey={s.key}
            isCompleted={completed}
            isOptional={s.optional}
          />
        );
      case "endpoint":
        return <AgentDeployStep isCompleted={completed} />;
      case "notifications":
        return <NotificationStep isCompleted={completed} />;
      case "verify":
        return (
          <VerifyStep
            steps={checklist?.steps ?? []}
            onComplete={onComplete}
          />
        );
      default:
        return null;
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* ── Header ──────────────────────────────────────── */}
      <header className="flex items-center justify-between border-b px-6 py-4">
        <div className="flex items-center gap-2.5">
          <img src="/logo.png" alt="" aria-hidden className="h-6 w-auto" />
          <span className="text-lg font-semibold tracking-tight">prayingmantis setup</span>
          <span className="ml-2 text-sm text-muted-foreground">
            {completedSteps}/{totalSteps} complete
          </span>
        </div>
        <Button variant="ghost" size="sm" onClick={onSkip}>
          Skip for now
        </Button>
      </header>

      {/* ── Progress bar ────────────────────────────────── */}
      <Progress value={progressPct} className="h-1 rounded-none" />

      {/* ── Main area ───────────────────────────────────── */}
      <div className="flex flex-1 flex-col md:flex-row">
        {/* ── Side stepper (vertical on md+, horizontal on mobile) ── */}
        <nav className="border-b md:border-b-0 md:border-r md:w-56 shrink-0">
          <div className="flex md:flex-col overflow-x-auto md:overflow-x-visible gap-1 p-3 md:p-4">
            {WIZARD_STEPS.map((s, idx) => {
              const completed = isStepCompleted(s, checklist);
              const isCurrent = idx === currentStep;

              return (
                <button
                  key={s.key}
                  onClick={() => setCurrentStep(idx)}
                  className={cn(
                    "flex items-center gap-2 rounded-md px-3 py-2 text-sm whitespace-nowrap transition-colors text-left",
                    isCurrent
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted",
                  )}
                >
                  {/* Step indicator */}
                  <span
                    className={cn(
                      "flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium",
                      completed && !isCurrent
                        ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400"
                        : isCurrent
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground",
                    )}
                  >
                    {completed && !isCurrent ? (
                      <Check className="h-3.5 w-3.5" />
                    ) : (
                      idx + 1
                    )}
                  </span>
                  <span className="hidden md:inline">{s.label}</span>
                </button>
              );
            })}
          </div>
        </nav>

        {/* ── Content panel ─────────────────────────────── */}
        <main className="flex-1 overflow-y-auto p-6 md:p-8 lg:p-10">
          <div className="mx-auto max-w-2xl">{renderStepContent()}</div>
        </main>
      </div>

      {/* ── Footer ──────────────────────────────────────── */}
      {showFooter && (
        <footer className="flex items-center justify-between border-t px-6 py-4">
          <Button
            variant="outline"
            size="sm"
            onClick={goBack}
            disabled={currentStep === 0}
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Back
          </Button>
          <Button size="sm" onClick={goNext}>
            Continue
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </footer>
      )}
    </div>
  );
}
